import React, { ComponentProps, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';

import EnvelopeIcon from 'buyside/svgs/envelope.svg';

import { CreateMessageInput, useCreateMessageMutation } from 'interfaces/graphql-typings';

import styles from './messageModalStyles.scss';

const defaultButtonOptions = {
  disabled: false,
  icon: EnvelopeIcon,
  size: BUTTON_SIZES.TINY,
  text: 'Message',
  type: BUTTON_TYPES.SECONDARY
};

export interface MessageModalProps {
  buttonOptions?: Partial<ComponentProps<typeof Button>>;
  isCurrentEmployer?: boolean;
  receiverEmployerName?: string;
  receiverId: string;
  receiverName: string;
}

const MessageModal: React.FC<MessageModalProps> = ({
  buttonOptions,
  isCurrentEmployer = true,
  receiverEmployerName,
  receiverId,
  receiverName
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [createMessage, { loading, error: mutationError }] = useCreateMessageMutation();

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError
  } = useForm<Pick<CreateMessageInput, 'body' | 'subject'>>();

  const closeModal = (): void => setModalOpen(false);

  const onSubmit = handleSubmit(async values => {
    const response = await createMessage({
      variables: { ...values, receiverUserId: receiverId }
    });

    const message = response.data?.createMessage;

    if (message) {
      setModalOpen(false);
      reset({ body: '', subject: '' });
      toast.success('Your message was sent successfully.', {
        autoClose: 6000,
        theme: 'colored'
      });
    }

    if (mutationError) setError('body', { type: 'custom', message: mutationError.message });
  });

  const renderOpenModalButton = useCallback(
    () => (
      <Button {...(buttonOptions || defaultButtonOptions)} onClick={() => setModalOpen(true)} />
    ),
    [buttonOptions]
  );

  return (
    <Modal
      isOpen={modalOpen}
      onClose={closeModal}
      renderOpenModalButton={renderOpenModalButton}
      title="New Message"
    >
      <form onSubmit={onSubmit}>
        <div>
          <div className={styles.labelItem}>
            <label>To</label>
            <div>
              <div>{receiverName}</div>
              {receiverEmployerName && (
                <div className={styles.employerName}>
                  {isCurrentEmployer ? '' : 'Formerly At '}
                  {receiverEmployerName}
                </div>
              )}
            </div>
          </div>
          <div className={styles.labelItem}>
            <label id="subjectLabel">Subject</label>
            <div className={styles.fullWidth}>
              <input
                {...register('subject')}
                aria-labelledby="subjectLabel"
                className={styles.subjectInput}
                disabled={loading}
                placeholder="(no subject)"
                type="text"
              />
            </div>
          </div>
          <div className={styles.labelItem}>
            <label id="bodyLabel" className={styles.bodyLabel}>
              Body
            </label>
            <div className={styles.fullWidth}>
              <textarea
                {...register('body', { required: 'Please enter a message' })}
                aria-labelledby="bodyLabel"
                className={styles.bodyInput}
                disabled={loading}
              />
            </div>
          </div>
        </div>
        {errors.body && <div className={styles.errorSection}>{errors.body.message}</div>}
        <div>
          <Button isLoading={loading} text="Send" type={BUTTON_TYPES.PRIMARY} />
          <Button
            disabled={loading}
            text="Cancel"
            onClick={closeModal}
            buttonType={BUTTON_TYPES.BUTTON}
          />
        </div>
      </form>
    </Modal>
  );
};

export default MessageModal;
